import { For, createMemo, createSignal, createContext, Accessor, Setter, createEffect } from 'solid-js';
import { createProxySignal, date, requireContext } from "common";
import { requireCalendarContext } from '../st-schedule-grid';
import { Dropdown, Select, ToolButton } from '../../..';
import { requireDropdownContext } from 'common';
import styles from '../st-schedule-grid.module.css';

const { addDays, addMonths, startOfMonth, monthName, truncateTime } = date;

class MonthGridContext {
    selYear: Accessor<number>; setSelYear: Setter<number>;
    selMonth: Accessor<number>; setSelMonth: Setter<number>;

    constructor() {
        [this.selYear, this.setSelYear] = createSignal(0);
        [this.selMonth, this.setSelMonth] = createSignal(0);
    }
}
const MonthGridContextJSX = createContext<MonthGridContext>();
function requireMonthGridContext() { return requireContext(MonthGridContextJSX) }

export default function MonthGrid() {
    const ctx = requireCalendarContext();
    const mgCtx = new MonthGridContext();

    return <MonthGridContextJSX.Provider value={mgCtx}>
        <Dropdown>
            <Dropdown.Trigger as="span" class="auto" style="text-align: center; cursor: pointer">
                {monthName(ctx.day())}
            </Dropdown.Trigger>
            <Dropdown.Content>
                <MonthGrid.Months />
            </Dropdown.Content>
        </Dropdown>
    </MonthGridContextJSX.Provider>
}

MonthGrid.Months = function () {
    const ctx = requireCalendarContext();
    const mgCtx = requireMonthGridContext();
    const ddCtx = requireDropdownContext();

    const months = createMemo(() => Array.from({ length: 12 }, (_, i) => i));
    const [selYearStr, setSelYearStr] = createProxySignal(
        () => mgCtx.selYear().toString(),
        (yr) => mgCtx.setSelYear(parseInt(yr))
    );

    function addYear(step: number) {
        const year = mgCtx.selYear() + step;
        const first = ctx.firstDate() ?? date.today();
        const last = ctx.lastDate() ?? first;

        if (year >= first.getFullYear() && year <= last.getFullYear()) {
            mgCtx.setSelYear(year);
        }
    }

    function Month(props: { month: number }) {
        const dt = createMemo(() => new Date(mgCtx.selYear(), props.month));
        const isSelectable = createMemo(() => ctx.monthEnabled(dt()));
        const hasAppointments = createMemo(() => {
            let start = startOfMonth(dt());
            const end = addMonths(start, 1);

            while (start.getTime() < end.getTime()) {
                start = addDays(start, 1);
                if (ctx.getAppointments(start).length > 0) {
                    return true;
                }
            }
            return false;
        });

        function selectMonth() {
            if (isSelectable()) {
                ddCtx.setIsOpen(false);
                ctx.setDay(new Date(mgCtx.selYear(), props.month));
            }
        }

        return <div class={styles.month}
            onClick={selectMonth}
            classList={{
                [styles['disabled']]: !isSelectable(),
                [styles['active']]: mgCtx.selMonth() == props.month,
                [styles['non-empty']]: hasAppointments()
            }}
        >
            {date.shortMonthName(dt())}
        </div>
    }

    createEffect(() => {
        const day = ctx.day();

        if (ddCtx.isOpen()) {
            mgCtx.setSelYear(day.getFullYear());
            mgCtx.setSelMonth(day.getMonth());
        }
    });

    return <div class="flexbox vertical">
        <div class="flexbox horizontal middle" style="margin-auto">
            <ToolButton icon="fa fa-chevron-left" label="Previous Year" onClick={() => addYear(-1)} />
            <Select value={[selYearStr, setSelYearStr]}>
                <Select.Trigger as="span" class="auto" style="text-align: center">
                    {mgCtx.selYear()}
                </Select.Trigger>
                <For each={ctx.years()}>{year =>
                    <Select.Item value={year}>{year}</Select.Item>
                }</For>
            </Select>
            <ToolButton icon="fa fa-chevron-right" label="Next Year" onClick={() => addYear(1)} />
        </div>
        <div class={styles['month-grid']}>
            <For each={months()}>{month =>
                <Month month={month} />
            }</For>
        </div>
    </div>
}
